import { useEffect, useState, type ReactNode } from 'react';
import { Button, HStack, Spinner, Stack, Text } from '@chakra-ui/react';
import { ChromeError } from './chrome_page.tsx';

// Asking something that takes time and can fail (a driver's choices, a test): the answer as a
// state, a line while waiting, and the reason with "Try again" when it failed.

export const messageOf = (error: unknown) : string => (error instanceof Error ? error.message : String(error));

export type Asked<T> = { status: 'waiting' } | { status: 'done'; value: T } | { status: 'failed'; reason: string };

/** Asks once per `key`; asks again when `key` changes or `retry` is called. */
export function useAsk<T>(key: string | undefined, ask: () => Promise<T>) : [Asked<T> | undefined, () => void] {
    const [attempt, setAttempt] = useState(0);
    const [asked, setAsked] = useState<Asked<T>>();

    useEffect(() => {
        if (key === undefined) {
            setAsked(undefined);

            return undefined;
        }

        let isCurrent = true;
        setAsked({ status: 'waiting' });
        ask().then(
            (value) => isCurrent && setAsked({ status: 'done', value }),
            (error: unknown) => isCurrent && setAsked({ status: 'failed', reason: messageOf(error) }),
        );

        return () => {
            isCurrent = false;
        };
        // `ask` is a new function each render; `key` says what it asks.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, attempt]);

    return [asked, () => setAttempt((count) => count + 1)];
}

export function Waiting({ text }: { text: string }) : ReactNode {
    return (
        <HStack gap="2" px="4" py="3" color="fg.muted">
            <Spinner size="sm" />
            <Text>{text}</Text>
        </HStack>
    );
}

export function Failed({ reason, onRetry }: { reason: string; onRetry: () => void }) : ReactNode {
    return (
        <Stack gap="2" px="4" py="3">
            <ChromeError text={reason} />
            <Button size="sm" variant="outline" alignSelf="flex-start" onClick={onRetry}>
                Try again
            </Button>
        </Stack>
    );
}
